/**
 * Learn / Learn++ analytics panel.
 *
 * Answers, in order: what are clinicians actually changing, who is changing
 * it, and is the system learning the right thing. Reads
 * /v1/analytics/learn-edits (admin/coordinator only), which queries the
 * instruction-change tables both learn flows write to.
 *
 * A note on coverage that matters when reading these numbers: a section only
 * gets a before/after row when its instruction text actually changed.
 * Sections the regression guard reverted, or the bloat cap rejected, appear
 * only in the Outcomes view — which is read from the loop metrics. So
 * "changes" counts committed learning, and "outcomes" counts every attempt.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from 'reactstrap';

import { changeSummary, diffWords, pairNoteRows } from '../../utils/noteDiff';

const VIEWS = [
  { key: 'sections', label: 'What’s changing', icon: 'fa fa-list-ul' },
  { key: 'by', label: 'Who & where', icon: 'fa fa-users' },
  { key: 'changes', label: 'Before / after', icon: 'fa fa-exchange' },
  { key: 'note_edits', label: 'What clinicians edit', icon: 'fa fa-pencil-square-o' },
  { key: 'outcomes', label: 'Outcomes', icon: 'fa fa-check-circle' },
  { key: 'drift', label: 'Instruction drift', icon: 'fa fa-line-chart' },
];

const DIMENSIONS = [
  { key: 'user', label: 'Clinician' },
  { key: 'emr', label: 'EMR' },
  { key: 'note_type', label: 'Note type' },
  { key: 'template', label: 'Template' },
  { key: 'clinic', label: 'Clinic' },
];

// note_edit_tags, in the order they answer the question "what are clinicians
// changing": what the scribe got wrong first, then what only the clinician
// could know, then the edits that change no facts at all.
const NOTE_EDIT_TAGS = [
  {
    key: 'scribe_missed',
    label: 'Scribe missed it',
    color: 'warning',
    hint: 'Said in the visit, left out of the note.',
  },
  {
    key: 'scribe_hallucinated',
    label: 'Scribe made it up',
    color: 'danger',
    hint: 'Not in the visit at all; the clinician corrected it.',
  },
  {
    key: 'clinician_supplied',
    label: 'Clinician knew it',
    color: 'info',
    hint: 'Not in the visit \u2014 added from what the clinician knew. The scribe could not have written it.',
  },
  {
    key: 'clinician_removed',
    label: 'Clinician cut it',
    color: 'secondary',
    hint: 'Said in the visit and written correctly, removed anyway.',
  },
  {
    key: 'conciseness',
    label: 'Said it shorter',
    color: 'light',
    hint: 'Same content, less text.',
  },
  {
    key: 'style_and_tone',
    label: 'Wording and style',
    color: 'light',
    hint: 'Preferences, abbreviations, acronyms \u2014 "The patient" to "The pt".',
  },
  {
    key: 'cosmetic',
    label: 'Cosmetic only',
    color: 'light',
    hint: 'Spacing, punctuation, capitalisation.',
  },
];

const NOTE_EDIT_TAG = Object.fromEntries(NOTE_EDIT_TAGS.map((t) => [t.key, t]));

const OUTCOME_STYLE = {
  committed: { color: 'success', label: 'Committed' },
  no_change: { color: 'secondary', label: 'No change' },
  rejected_bloat: { color: 'warning', label: 'Rejected — too long' },
  rejected_collapse: { color: 'warning', label: 'Rejected — too short' },
  reverted_below_min_accuracy: { color: 'danger', label: 'Reverted — too far off' },
  reverted_worse_than_baseline: { color: 'danger', label: 'Reverted — got worse' },
};

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const pct = (v) =>
  v === null || v === undefined || v === '' ? '—' : `${(Number(v) * 100).toFixed(0)}%`;

const signedPct = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v) * 100;
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
};

const LearnEditsPanel = ({ apiClient }) => {
  const [view, setView] = useState('sections');
  const [dimension, setDimension] = useState('user');
  const [t1, setT1] = useState(isoDaysAgo(30));
  const [t2, setT2] = useState(isoDaysAgo(0));
  const [source, setSource] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [noteType, setNoteType] = useState('');
  const [emrUrl, setEmrUrl] = useState('');
  const [sectionKey, setSectionKey] = useState('');
  const [search, setSearch] = useState('');

  const [overview, setOverview] = useState([]);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({
    users: [],
    emrs: [],
    note_types: [],
    templates: [],
  });
  const [expanded, setExpanded] = useState(null);
  const [editTag, setEditTag] = useState('');
  const [topics, setTopics] = useState([]);
  const [notesById, setNotesById] = useState({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const filters = useMemo(
    () => ({
      source: source || undefined,
      userEmail: userEmail || undefined,
      emrUrl: emrUrl || undefined,
      noteType: noteType || undefined,
    }),
    [source, userEmail, emrUrl, noteType],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const viewOptions = { ...filters, limit: 200 };
      if (view === 'by') viewOptions.dimension = dimension;
      if (view === 'changes') {
        if (sectionKey) viewOptions.sectionKey = sectionKey;
        if (search) viewOptions.search = search;
      }

      const requests = [
        apiClient.getLearnEdits('overview', t1, t2, filters),
        apiClient.getLearnEdits(view, t1, t2, viewOptions),
      ];
      // The tag table alone says an edit added information; the topics say
      // what it was about. Both are wanted at once, so they load together.
      if (view === 'note_edits') {
        requests.push(
          apiClient.getLearnEdits('note_topics', t1, t2, {
            ...filters,
            tag: editTag || undefined,
            limit: 40,
          }),
        );
      }
      const [summaryRsp, viewRsp, topicsRsp] = await Promise.all(requests);

      if (summaryRsp?.error || viewRsp?.error) {
        setError(viewRsp?.error || summaryRsp?.error);
        setOverview([]);
        setRows([]);
        setTopics([]);
      } else {
        setOverview(summaryRsp?.rows || []);
        setRows(viewRsp?.rows || []);
        setTopics(topicsRsp?.rows || []);
      }
    } catch (e) {
      setError('Could not load learn analytics.');
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, view, dimension, t1, t2, filters, sectionKey, search, editTag]);

  useEffect(() => {
    load();
  }, [load]);

  // Dropdown values, refreshed when the date window moves or the EMR changes.
  //
  // The EMR filter is passed through, so note types / templates / clinicians
  // come back scoped to the selected EMR — "PT Daily Note" in WebPT and in
  // Raintree are different note types in practice, and an unscoped list of
  // every note type across every EMR is not a list anyone can use.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .getLearnEdits('filters', t1, t2, { emrUrl: emrUrl || undefined })
      .then((rsp) => {
        if (cancelled) return;
        if (rsp?.error) {
          // Don't swallow this — a failing filters query used to leave the
          // dropdowns silently showing only "All".
          // eslint-disable-next-line no-console
          console.warn('Learn++ filter options failed:', rsp.error);
          return;
        }
        const next = rsp?.rows?.[0];
        if (!next) return;
        setOptions((prev) => ({
          ...next,
          // When an EMR is selected the query only returns that one value, so
          // keep the full list — otherwise picking an EMR collapses the very
          // dropdown you'd use to pick a different one.
          emrs: emrUrl ? prev.emrs : next.emrs,
        }));
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('Learn++ filter options failed:', e);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, t1, t2, emrUrl]);

  // Fetched per card rather than with the list: the note text would multiply
  // the size of every page, and most rows are never opened.
  const loadNote = useCallback(
    async (eventId) => {
      if (!eventId || notesById[eventId]) return;
      setNotesById((prev) => ({ ...prev, [eventId]: { loading: true } }));
      let next;
      try {
        const rsp = await apiClient.getLearnEdits('note', t1, t2, { eventId });
        const sections = rsp?.rows || [];
        if (rsp?.error) {
          next = { error: rsp.error };
        } else if (!sections.length) {
          next = { missing: true };
        } else {
          // Back into the two-note shape the differ takes.
          const ai = {};
          const edited = {};
          sections.forEach((row) => {
            ai[row.section_key] = row.ai_note;
            edited[row.section_key] = row.edited_note;
          });
          next = { rows: pairNoteRows(ai, edited) };
        }
      } catch (e) {
        next = { error: 'Could not load the note.' };
      }
      setNotesById((prev) => ({ ...prev, [eventId]: next }));
    },
    [apiClient, t1, t2, notesById],
  );

  const totals = useMemo(() => {
    const learnPlus = overview.find((r) => r.source === 'learn_plus') || {};
    const legacy = overview.find((r) => r.source === 'learn') || {};
    return { learnPlus, legacy };
  }, [overview]);

  const renderSummary = () => {
    const cards = [
      {
        label: 'Learn++ runs',
        value: totals.learnPlus.learns || 0,
        sub: `${totals.learnPlus.clinicians || 0} clinicians`,
      },
      {
        label: 'Avg improvement',
        value: signedPct(totals.learnPlus.avg_gain),
        sub: `${pct(totals.learnPlus.avg_baseline)} → ${pct(totals.learnPlus.avg_best)}`,
        hint: 'How much closer the AI got to the clinician’s note after learning.',
      },
      {
        label: 'Effort per learn',
        value: `${Number(totals.learnPlus.avg_passes || 0).toFixed(1)} passes`,
        sub: `${Number(totals.learnPlus.avg_revisions || 0).toFixed(1)} rewrites`,
      },
      {
        label: 'Legacy Learn runs',
        value: totals.legacy.learns || 0,
        sub: 'for comparison',
      },
    ];
    return (
      <Row className="mb-3">
        {cards.map((c) => (
          <Col md="3" key={c.label}>
            <Card className="shadow-sm h-100">
              <CardBody className="py-3">
                <div className="text-uppercase text-muted" style={{ fontSize: 11 }}>
                  {c.label}
                </div>
                <div style={{ fontSize: 26, fontWeight: 600 }}>{c.value}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {c.sub}
                </div>
                {c.hint && (
                  <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                    {c.hint}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderFilters = () => (
    <Row className="mb-3">
      <Col md="2">
        <label className="form-control-label">From</label>
        <Input type="date" value={t1} onChange={(e) => setT1(e.target.value)} />
      </Col>
      <Col md="2">
        <label className="form-control-label">To</label>
        <Input type="date" value={t2} onChange={(e) => setT2(e.target.value)} />
      </Col>
      <Col md="3">
        <label className="form-control-label">Clinician</label>
        <Input
          list="learn-users"
          placeholder="all clinicians"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
        />
        <datalist id="learn-users">
          {(options.users || []).map((u) => (
            <option key={u} value={u} />
          ))}
        </datalist>
      </Col>
      <Col md="2">
        <label className="form-control-label">
          EMR{' '}
          {options.emrs?.length ? (
            <span className="text-muted">({options.emrs.length})</span>
          ) : null}
        </label>
        <Input
          type="select"
          value={emrUrl}
          onChange={(e) => {
            setEmrUrl(e.target.value);
            // The note types below are about to be re-fetched for the new EMR;
            // a note type held over from the previous one would filter
            // everything out and look like "no data".
            setNoteType('');
          }}
        >
          <option value="">All</option>
          {(options.emrs || []).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Input>
      </Col>
      <Col md="2">
        <label className="form-control-label">
          Note type{' '}
          {options.note_types?.length ? (
            <span className="text-muted">({options.note_types.length})</span>
          ) : null}
        </label>
        <Input type="select" value={noteType} onChange={(e) => setNoteType(e.target.value)}>
          <option value="">All</option>
          {(options.note_types || []).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Input>
      </Col>
      <Col md="1">
        <label className="form-control-label">Flow</label>
        <Input type="select" value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">Both</option>
          <option value="learn_plus">Learn++</option>
          <option value="learn">Legacy</option>
        </Input>
      </Col>
    </Row>
  );

  const renderSections = () => (
    <Table responsive hover className="align-items-center">
      <thead className="thead-light">
        <tr>
          <th>Section</th>
          <th>Times changed</th>
          <th>Clinicians</th>
          <th>Avg length change</th>
          <th>Last changed</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.section_key}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSectionKey(r.section_key);
              setView('changes');
            }}
          >
            <td>
              <strong>{r.section_key}</strong>
            </td>
            <td>{r.changes}</td>
            <td>{r.clinicians}</td>
            <td
              className={
                Number(r.avg_length_delta) > 50 ? 'text-warning' : 'text-muted'
              }
            >
              {Number(r.avg_length_delta) >= 0 ? '+' : ''}
              {Number(r.avg_length_delta || 0).toFixed(0)} chars
            </td>
            <td className="text-muted">{(r.last_changed || '').slice(0, 16)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const renderBy = () => (
    <>
      <Nav pills className="mb-3">
        {DIMENSIONS.map((d) => (
          <NavItem key={d.key}>
            <NavLink
              tag="button"
              type="button"
              className={dimension === d.key ? 'active' : ''}
              onClick={() => setDimension(d.key)}
            >
              {d.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      <Table responsive hover className="align-items-center">
        <thead className="thead-light">
          <tr>
            <th>{DIMENSIONS.find((d) => d.key === dimension)?.label}</th>
            <th>Learns</th>
            <th>Sections changed</th>
            <th>Avg improvement</th>
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td>
                <strong>{r.label}</strong>
              </td>
              <td>{r.learns}</td>
              <td>{r.section_changes}</td>
              <td className={Number(r.avg_gain) > 0 ? 'text-success' : 'text-muted'}>
                {signedPct(r.avg_gain)}
              </td>
              <td className="text-muted">{(r.last_activity || '').slice(0, 16)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );

  const renderOutcomes = () => (
    <Table responsive className="align-items-center">
      <thead className="thead-light">
        <tr>
          <th>Section</th>
          <th>Outcome</th>
          <th>Count</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const style = OUTCOME_STYLE[r.outcome] || { color: 'secondary', label: r.outcome };
          return (
            <tr key={`${r.section_key}-${r.outcome}-${i}`}>
              <td>{r.section_key}</td>
              <td>
                <Badge color={style.color}>{style.label}</Badge>
              </td>
              <td>{r.count}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  const renderDrift = () => (
    <Table responsive className="align-items-center">
      <thead className="thead-light">
        <tr>
          <th>When</th>
          <th>Clinician</th>
          <th>Template</th>
          <th>Section</th>
          <th>Length before → after</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const delta = Number(r.length_after) - Number(r.length_before);
          return (
            <tr key={i}>
              <td className="text-muted">{(r.created_at || '').slice(0, 16)}</td>
              <td>{r.user_id}</td>
              <td>{r.template_name}</td>
              <td>{r.section_key}</td>
              <td>
                {r.length_before} → {r.length_after}{' '}
                <Badge color={delta > 100 ? 'warning' : delta > 0 ? 'light' : 'success'}>
                  {delta >= 0 ? '+' : ''}
                  {delta}
                </Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );

  const renderNoteEdits = () => (
    <>
      <div className="text-muted mb-3" style={{ fontSize: 12 }}>
        Every signed note whose edits cleared the tagging threshold, classified
        against the visit transcript. A section carries more than one tag when
        more than one kind of edit applies, so the counts overlap.
      </div>
      <Table responsive hover className="align-items-center">
        <thead className="thead-light">
          <tr>
            <th>Kind of edit</th>
            <th>Sections</th>
            <th>Notes</th>
            <th>Clinicians</th>
            <th>Typical note rewritten</th>
            <th>Typical length change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const tag = NOTE_EDIT_TAG[r.tag] || { label: r.tag, color: 'secondary' };
            const active = editTag === r.tag;
            return (
              <tr
                key={r.tag}
                style={{ cursor: 'pointer' }}
                className={active ? 'table-primary' : ''}
                onClick={() => setEditTag(active ? '' : r.tag)}
              >
                <td>
                  <Badge color={tag.color}>{tag.label}</Badge>
                  {tag.hint && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {tag.hint}
                    </div>
                  )}
                </td>
                <td>{r.sections}</td>
                <td>{r.notes}</td>
                <td>{r.clinicians}</td>
                <td>{pct(Number(r.median_pct_changed || 0) / 100)}</td>
                <td className="text-muted">
                  {Number(r.median_length_delta) >= 0 ? '+' : ''}
                  {Number(r.median_length_delta || 0).toFixed(0)} chars
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="text-uppercase text-muted mt-4 mb-2" style={{ fontSize: 11 }}>
        What the edits were about
        {editTag ? ` \u2014 ${(NOTE_EDIT_TAG[editTag] || {}).label || editTag}` : ''}
      </div>
      {!topics.length ? (
        <div className="text-muted" style={{ fontSize: 13 }}>
          No topics recorded for these filters.
        </div>
      ) : (
        <Row>
          {topics.map((t) => (
            <Col md="4" key={t.topic}>
              <div className="d-flex justify-content-between py-1" style={{ fontSize: 13 }}>
                <span>{t.topic}</span>
                <span className="text-muted">
                  {t.sections} &middot; {t.clinicians} clin.
                </span>
              </div>
            </Col>
          ))}
        </Row>
      )}
    </>
  );

  // Comprehend's note on the left, what the clinician signed on the right,
  // with the words each side lost or gained marked. The instruction diff above
  // says what was learned; this says what it was learned from.
  const renderNoteComparison = (eventId) => {
    const state = notesById[eventId];
    if (!state) return null;
    if (state.loading) {
      return (
        <div className="text-muted py-3" style={{ fontSize: 13 }}>
          <Spinner size="sm" color="primary" /> Loading the note&hellip;
        </div>
      );
    }
    if (state.error) {
      return (
        <div className="text-muted py-3" style={{ fontSize: 13 }}>
          Could not load the note: {state.error}
        </div>
      );
    }
    if (state.missing) {
      return (
        <div className="text-muted py-3" style={{ fontSize: 13 }}>
          No note recorded for this learn. The pair is stored when the learn
          runs, so learns from before that shipped have nothing to compare.
        </div>
      );
    }

    const changed = state.rows.filter((row) => row.before !== row.after);
    const visible = showUnchanged ? state.rows : changed;
    const mark = (segments, keep, background) =>
      segments
        .filter((seg) => seg.type === 'same' || seg.type === keep)
        .map((seg, i) =>
          seg.type === 'same' ? (
            <span key={i}>{seg.text}</span>
          ) : (
            <span key={i} style={{ background }}>
              {seg.text}
            </span>
          ),
        );

    return (
      <>
        <hr className="my-3" />
        <Row className="align-items-center mb-2">
          <Col md="8">
            <div className="text-uppercase text-muted" style={{ fontSize: 11 }}>
              The note this was learned from
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {changed.length} of {state.rows.length} fields changed
            </div>
          </Col>
          <Col md="4" className="text-right">
            <Button
              size="sm"
              color="secondary"
              outline
              onClick={() => setShowUnchanged((v) => !v)}
            >
              {showUnchanged ? 'Only changed fields' : 'Show the whole note'}
            </Button>
          </Col>
        </Row>
        {!visible.length ? (
          <div className="text-muted" style={{ fontSize: 13 }}>
            The clinician signed this note unchanged.
          </div>
        ) : (
          <Table responsive className="align-items-center" style={{ fontSize: 12 }}>
            <thead className="thead-light">
              <tr>
                <th style={{ width: '18%' }}>Field</th>
                <th style={{ width: '41%' }}>Comprehend wrote</th>
                <th style={{ width: '41%' }}>Clinician signed</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const segments = diffWords(row.before, row.after);
                const summary = changeSummary(row.before, row.after);
                return (
                  <tr key={`${row.section}-${row.field}`}>
                    <td>
                      <strong>{row.section}</strong>
                      {row.field && (
                        <div className="text-muted">{row.field}</div>
                      )}
                      {!summary.unchanged && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          &minus;{summary.removed} / +{summary.added} words
                        </div>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>
                      {mark(segments, 'removed', '#fdd8d8')}
                    </td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>
                      {mark(segments, 'added', '#d4f5dd')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </>
    );
  };

  const renderChanges = () => (
    <>
      <Row className="mb-3">
        <Col md="4">
          <Input
            placeholder="Section (e.g. Subjective)"
            value={sectionKey}
            onChange={(e) => setSectionKey(e.target.value)}
          />
        </Col>
        <Col md="6">
          <Input
            placeholder="Search instruction or justification (e.g. outcome measure, bullet)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col md="2">
          <Button color="primary" block onClick={load}>
            Search
          </Button>
        </Col>
      </Row>
      {rows.map((r, i) => {
        const key = `${r.event_id}-${r.section_key}-${i}`;
        const open = expanded === key;
        return (
          <Card key={key} className="mb-2 shadow-sm">
            <CardHeader
              className="py-2"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setExpanded(open ? null : key);
                if (!open) loadNote(r.event_id);
              }}
            >
              <Row className="align-items-center">
                <Col md="3">
                  <strong>{r.section_key || '(no section changed)'}</strong>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {r.user_id}
                  </div>
                </Col>
                <Col md="3" className="text-muted" style={{ fontSize: 12 }}>
                  {r.template_name}
                  <br />
                  {r.note_type}
                </Col>
                <Col md="3">
                  <Badge color={r.source === 'learn_plus' ? 'primary' : 'secondary'}>
                    {r.source === 'learn_plus' ? 'Learn++' : 'Legacy'}
                  </Badge>{' '}
                  {r.outcome && (
                    <Badge color={(OUTCOME_STYLE[r.outcome] || {}).color || 'secondary'}>
                      {(OUTCOME_STYLE[r.outcome] || {}).label || r.outcome}
                    </Badge>
                  )}
                  {r.baseline_accuracy && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {pct(r.baseline_accuracy)} → {pct(r.best_accuracy)}
                    </div>
                  )}
                </Col>
                <Col md="3" className="text-right text-muted" style={{ fontSize: 12 }}>
                  {(r.created_at || '').slice(0, 16)}
                  <i className={`fa fa-chevron-${open ? 'up' : 'down'} ml-2`} />
                </Col>
              </Row>
              {/* The model's own account of what it learned. Shown collapsed
                  because for small edits it's more informative than the
                  score, which can't resolve them. */}
              {r.justification ? (
                <div
                  className="mt-2 pl-2"
                  style={{ fontSize: 13, borderLeft: '3px solid #5e72e4' }}
                >
                  <i className="fa fa-quote-left text-muted mr-1" style={{ fontSize: 10 }} />
                  {r.justification}
                </div>
              ) : (
                <div className="mt-2 text-muted" style={{ fontSize: 12, fontStyle: 'italic' }}>
                  No justification recorded.
                </div>
              )}
            </CardHeader>
            {open && (
              <CardBody className="py-3">
                <Row>
                  <Col md="6">
                    <div className="text-uppercase text-muted mb-1" style={{ fontSize: 11 }}>
                      Before
                    </div>
                    <pre className="p-2 bg-light" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {r.previous_instruction || '—'}
                    </pre>
                  </Col>
                  <Col md="6">
                    <div className="text-uppercase text-muted mb-1" style={{ fontSize: 11 }}>
                      After
                    </div>
                    <pre className="p-2 bg-light" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {r.new_instruction || '—'}
                    </pre>
                  </Col>
                </Row>
                <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.7 }}>
                  <div>
                    <strong>Clinician</strong> {r.user_id || '—'}
                    {r.clinic_id ? ` · Clinic ${r.clinic_id}` : ''}
                  </div>
                  <div>
                    <strong>EMR</strong> {r.emr_url || '—'} ·{' '}
                    <strong>Note type</strong> {r.note_type || '—'} ·{' '}
                    <strong>Template</strong> {r.template_name || '—'} ({r.config_id})
                  </div>
                  <div>
                    <strong>Conversation</strong> {r.conversation_id} ·{' '}
                    {r.revisions || 0} rewrites · stopped: {r.stopped_reason || '—'}
                    {r.outcome ? ` · outcome: ${r.outcome}` : ''}
                  </div>
                </div>
                {renderNoteComparison(r.event_id)}
              </CardBody>
            )}
          </Card>
        );
      })}
    </>
  );

  const body = () => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      );
    }
    if (error) {
      return (
        <div className="alert alert-warning">
          {error}
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            If this says the notes database isn’t configured, the environment is
            missing its NOTES_DB_* settings.
          </div>
        </div>
      );
    }
    if (!rows.length) {
      return (
        <div className="text-muted text-center py-5">
          No learns recorded for these filters.
        </div>
      );
    }
    if (view === 'sections') return renderSections();
    if (view === 'by') return renderBy();
    if (view === 'note_edits') return renderNoteEdits();
    if (view === 'outcomes') return renderOutcomes();
    if (view === 'drift') return renderDrift();
    return renderChanges();
  };

  return (
    <div className="learn-edits-panel">
      {renderFilters()}
      {renderSummary()}
      <Nav tabs className="analytics-tabs" role="tablist">
        {VIEWS.map((v) => (
          <NavItem key={v.key}>
            <NavLink
              tag="button"
              type="button"
              role="tab"
              aria-selected={view === v.key}
              className={view === v.key ? 'active' : ''}
              onClick={() => setView(v.key)}
            >
              <i className={v.icon} style={{ marginRight: 8 }} />
              {v.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      <div className="tab-content pt-3">{body()}</div>
    </div>
  );
};

export default LearnEditsPanel;
